import type { AutopopAttempts, AutopopStepKey } from './types.js';

/**
 * Auto-populate gate — the PURE "which passes should run?" logic, shared by the client sequencer
 * (apps/web/src/lib/autopop.ts + the DealDetail hook) and unit-tested here. No I/O.
 *
 * Two independent questions decide whether a pass runs on an AUTOMATIC (first-open) run:
 *
 *  1. `shouldRunStep` — is the pass's output still MISSING on the deal? (blanks-only: a pass never
 *     re-does work that already landed, and never clobbers curated data.)
 *  2. `hasAttempt`    — has the pass already RUN for this deal at least once?
 *
 * Both must say yes. (1) alone is not enough: a pass that runs and legitimately produces nothing
 * (no extractable deck text, no people to research, model returned nothing groundable) leaves its
 * column null, so (1) stays true forever — which is exactly why first-open auto-populate used to
 * re-fire on every open of such a deal, showing the loader and re-spending each time. The attempt
 * ledger makes "auto-populate runs once per deal" literally true.
 *
 * A FORCE run (the ↻ Auto-populate / ↻ Refresh buttons) ignores both and re-runs everything.
 */

/** Every pass, in run order. Mirrors AUTOPOP_STEPS in the web sequencer. */
export const AUTOPOP_STEP_KEYS: readonly AutopopStepKey[] = [
  'deck', 'briefing', 'questions', 'research', 'registry', 'team', 'industry', 'peers', 'score', 'moduleRatings',
];

/** The subset of deal fields the gate reads. */
export interface AutopopDealState {
  deck?: boolean | null;
  dataroom?: boolean | null; // a WhatsApp data-room deal also warrants auto-populate (web enrichment)
  business?: string | null;
  pnl?: unknown[] | null;
  team?: unknown[] | null;
  intel?: { directors?: unknown[] | null; leadership?: unknown[] | null } | null;
  industryDetail?: unknown | null;
  peerRows?: unknown[] | null;
  research?: unknown | null;
  businessBrief?: unknown | null;
  teamResearch?: unknown | null;
  promoterQuestions?: unknown | null;
  llmScore?: unknown | null;
  moduleRatingsAI?: unknown | null;
  autopopAttempts?: AutopopAttempts | null;
}

const len = (v: unknown): number => (Array.isArray(v) ? v.length : 0);
const filled = (v: unknown): boolean => typeof v === 'string' && v.trim().length > 0;

/**
 * Is this pass's output still missing? Blanks-only: a pass is SKIPPED when the deal already carries
 * what it would produce, so the sequence fills only what's absent.
 */
export function shouldRunStep(step: AutopopStepKey, d: AutopopDealState): boolean {
  switch (step) {
    case 'deck':
      // Deck already extracted → business summary OR a P&L table OR a team is present.
      return !(filled(d.business) || len(d.pnl) > 0 || len(d.team) > 0);
    case 'briefing':
      // Deck-mined briefing already built → businessBrief present.
      return d.businessBrief == null;
    case 'questions':
      // Promoter questions already generated → promoterQuestions present.
      return d.promoterQuestions == null;
    case 'score':
      // LLM overall read already computed → llmScore present.
      return d.llmScore == null;
    case 'moduleRatings':
      // Module ratings already AI-suggested (and, when the sliders were still untouched, applied)
      // → moduleRatingsAI present. Runs after 'score' — same enriched corpus, so it goes last too.
      return d.moduleRatingsAI == null;
    case 'registry':
      return !(len(d.intel?.directors) > 0 || len(d.intel?.leadership) > 0);
    case 'team':
      // Per-person team research already built → teamResearch present.
      return d.teamResearch == null;
    case 'industry':
      return d.industryDetail == null;
    case 'peers':
      return len(d.peerRows) === 0;
    case 'research':
      return d.research == null;
    default:
      return false;
  }
}

/** Has this pass already run for this deal (whatever the outcome)? */
export function hasAttempt(step: AutopopStepKey, d: AutopopDealState): boolean {
  return d.autopopAttempts?.[step] != null;
}

/**
 * Should this pass run on an AUTOMATIC (non-forced) run? Output missing AND never attempted.
 * A pass whose recorded attempt came back `empty` or `error` waits for a manual ↻ — the errors
 * banner on the deal page tells the analyst which ones and offers the retry.
 */
export function shouldAutoRunStep(step: AutopopStepKey, d: AutopopDealState): boolean {
  return shouldRunStep(step, d) && !hasAttempt(step, d);
}

/**
 * Does this deal warrant a first-open auto-populate run at all? A deck-bearing deal with at least
 * one never-attempted pass whose output is still missing. Deals enriched before a new pass existed
 * backfill correctly (the new pass has no ledger entry, so it runs once).
 */
export function needsAutoPopulate(d: AutopopDealState): boolean {
  return !!(d.deck || d.dataroom) && AUTOPOP_STEP_KEYS.some((k) => shouldAutoRunStep(k, d));
}

/**
 * Passes that ran but produced nothing / failed, so the analyst can see WHY a tab is empty and
 * retry deliberately. Ordered by run order.
 */
export function unproductiveAttempts(d: AutopopDealState): { key: AutopopStepKey; outcome: 'empty' | 'error'; note?: string }[] {
  const out: { key: AutopopStepKey; outcome: 'empty' | 'error'; note?: string }[] = [];
  for (const key of AUTOPOP_STEP_KEYS) {
    const a = d.autopopAttempts?.[key];
    if (!a || a.outcome === 'filled') continue;
    // Only worth surfacing while the output is still missing — a later run may have filled it.
    if (!shouldRunStep(key, d)) continue;
    out.push({ key, outcome: a.outcome, ...(a.note ? { note: a.note } : {}) });
  }
  return out;
}
